import React, { useEffect, useRef, useState } from "react";
import styles from "./index.module.css";
import PaybackIcon from "../../images/icons/payback.svg";
import ZeropcIcon from "../../images/icons/zero_pc.svg";
import Navbar from "../../components/molecules/Navbar";
import Footer from "../../components/molecules/Footer";
import Button from "../../components/atoms/Button";
import { useLocation, useNavigate } from "react-router-dom";
import { newFormatToIndianRupees } from "../../utils/formatToIndianRpuees";
import moment from "moment";
import { ApplicantStage } from "../../utils/applicationStage";

function SelectEmi() {
  const navigate = useNavigate();
  const location = useLocation();
const [product, setProduct] = useState<{
    id?: number;
    applicationId?: string;
    fundCode?: string;
    productId?: string;
    loanAmount?: number;
    emi?: number;
    emiFirstDate?: string;
    dayOfEmi?: number;
    sanctionLetterUrl?: string;
    productDetails?: any;
  }>({});
  const data = sessionStorage.getItem("data");
  
  let [userData, setUserData] = useState({
    application: '',
    dob: '',
    applicationId: "",
    userId: "",
    email: "",
    panId: "",
    dateOfBirth: "",
    instituteId: "",
    firstName: "",
    lastName: "",
    mobile: "",
    course: "",
    fees: 0,
    studentName: "",
    instituteName: "",
    redirectUrl: "",
    channelId: 0,
    flow: ''
  });

  // Loading states and call tracking
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingStage, setIsLoadingStage] = useState(false);
  const productCallMade = useRef(false);
  const stageCallMade = useRef(false);

  // Load userData from sessionStorage
  useEffect(() => {
    if (data) {
      try {
        const jsonData = atob(data);
        setUserData(JSON.parse(jsonData));
      } catch (error) {
        console.error('Error parsing userData:', error);
      }
    }
  }, [data]);

  // Reset call flags when applicationId changes
  useEffect(() => {
    productCallMade.current = false;
    stageCallMade.current = false;
  }, [userData?.applicationId, location?.state?.data?.applicationId]);

  // Product Details API Call
  useEffect(() => {
    const getProductsDetails = async () => {
      // Prevent duplicate calls
      if (productCallMade.current || isLoadingProducts) return;
      
      const applicationId = userData?.applicationId || 
        (location?.state?.data?.existing ? 
          location?.state?.data?.applicationId : 
          location?.state?.data?.application);

      // Validate required data
      if (!applicationId) {
        console.log('No applicationId available for product details');
        return;
      }

      const memberId = userData?.channelId === 12 ? 
        "7b75d903-dabf-4571-9052-61c825403004" : 
        location?.state?.data?.userId;

      if (!memberId) {
        console.log('No memberId available for auth');
        return;
      }

      productCallMade.current = true;
      setIsLoadingProducts(true);
      
      try {
        console.log('Fetching sanction details for applicationId:', applicationId);
        
        // First API call - Sanction Details
        const response = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/sanction-details?applicationId=${applicationId}`
        );

        if (!response.ok) {
          throw new Error(`Sanction details API failed: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Sanction details result:', result);
        
        if (!result.data || !result.data.productId) {
          throw new Error('Invalid response: missing productId in sanction details');
        }

        setProduct(result.data);

        console.log('Authenticating with memberId:', memberId);
        
        // Second API call - Authentication
        const response1 = await fetch(`${process.env.REACT_APP_DASHBOARD_URL}/login/auth`, {
          method: "POST",
          body: JSON.stringify({
            memberId: memberId,
          }),
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response1.ok) {
          throw new Error(`Auth API failed: ${response1.status} ${response1.statusText}`);
        }

        const result1 = await response1.json();
        console.log('Auth result:', result1);

        if (!result1.data) {
          throw new Error('Invalid auth response: missing token');
        }

        const authToken = result1.data;

        console.log('Fetching product details for productId:', result.data.productId);
        
        // Third API call - Product Details
        const response2 = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/products/id?productId=${result.data.productId}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response2.ok) {
          throw new Error(`Product details API failed: ${response2.status} ${response2.statusText}`);
        }

        const result2 = await response2.json();
        console.log('Product details result:', result2);

        setProduct((prev) => ({
          ...prev,
          productDetails: result2.data,
        }));

      } catch (error) {
        console.error('Error in getProductsDetails:', error);
        productCallMade.current = false; // Reset on error to allow retry
      } finally {
        setIsLoadingProducts(false);
      }
    };

    // Only call when we have the necessary data
    const hasRequiredData = userData?.applicationId || location?.state?.data;
    if (hasRequiredData && !productCallMade.current && !isLoadingProducts) {
      getProductsDetails();
    }
  }, [userData?.applicationId, location?.state?.data?.applicationId, location?.state?.data?.existing, location?.state?.data?.userId, userData?.channelId]);

  // Application Stage Check
  useEffect(() => {
    const checkApplicationStage = async () => {
      // Prevent duplicate calls
      if (stageCallMade.current || isLoadingStage) return;

      const applicationId = userData?.applicationId || 
        (location?.state?.data?.existing ? 
          location?.state?.data?.applicationId : 
          location?.state?.data?.application);

      // Validate required data
      if (!applicationId) {
        console.log('No applicationId available for stage check');
        return;
      }

      stageCallMade.current = true;
      setIsLoadingStage(true);

      try {
        console.log('Checking application stage for applicationId:', applicationId);
        
        const response = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${applicationId}`
        );

        if (!response.ok) {
          throw new Error(`Stage check API failed: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Stage check result:', result);

        if (result?.data?.stage) {
          console.log('Current stage:', result.data.stage);
          
          switch (result.data.stage) {
            case ApplicantStage.PanVerification:
              navigate(`/${atob(JSON.stringify(location?.state?.data))}`);
              break;
            case ApplicantStage.EmploymentDetails:
              navigate("/work-details", {
                state: { data: location?.state?.data },
              });
              break;
            case ApplicantStage.AddressDetails:
              navigate("/address-details", { state: { data: location?.state?.data } });
              break;
            case ApplicantStage.BankStatement:
              navigate("/view-offers", {
                state: { data: location?.state?.data },
              });
              break;
            case ApplicantStage.AwaitingApproval:
            case ApplicantStage.CoapplicantRequired:
            case ApplicantStage.AwaitingCoapplicantConsent:
              navigate("/parking-page", {
                state: { data: location?.state?.data },
              });
              break;
            case ApplicantStage.Approved:
              // Stay on current page (select-emi)
              console.log('Application approved - staying on select-emi page');
              break;
            case ApplicantStage.SanctionTermsAccepted:
            case ApplicantStage.Digilocker:
            case ApplicantStage.Selfie:
            case ApplicantStage.Agreement:
            case ApplicantStage.BankAccountVerified:
              navigate("/sanctions", {
                state: { data: location?.state?.data },
              });
              break;
            case ApplicantStage.Mandate:
              navigate("/pf-collection", {
                state: { data: location?.state?.data },
              });
              break;
            case ApplicantStage.ProcessComplete:
            case ApplicantStage.Disbursed:
              navigate("/disbursed-page", {
                state: { data: location?.state?.data },
              });
              break;
            case ApplicantStage.PFCollections:
              navigate("/pf-collection", {
                state: { data: location?.state?.data },
              });
              break;
            case ApplicantStage.Rejected:
              navigate("/rejected-page", {
                state: { data: location?.state?.data },
              });
              break;
            default:
              try {
                navigate(`/${atob(JSON.stringify(location?.state?.data))}`);
              } catch (err) {
                console.error('Error decoding location data:', err);
                navigate(`/enter-pan`);
              }
              break;
          }
        }
      } catch (error) {
        console.error('Error checking application stage:', error);
        stageCallMade.current = false; // Reset on error to allow retry
      } finally {
        setIsLoadingStage(false);
      }
    };

    // Only call when we have the necessary data
    const hasRequiredData = userData?.applicationId || location?.state?.data?.applicationId || location?.state?.data;
    if (hasRequiredData && !stageCallMade.current && !isLoadingStage) {
      checkApplicationStage();
    }
  }, [userData?.applicationId, location?.state?.data?.applicationId, location?.state?.data?.existing]);

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.container}>
          <div className={styles.content}>
            <div
              style={{
                flex: 1,
                marginLeft: "1em",
              }}
            >
              <p
                style={{
                  color: "#606060",
                  fontSize: "0.8em",
                  fontWeight: 'bold'
                }}
              >
                Principal amount
              </p>
              <p
                style={{
                  fontWeight: 600,
                  fontSize: "1em",
                }}
              >
                {newFormatToIndianRupees(product?.loanAmount || 0)}
              </p>
            </div>
            <div className={styles.advert}>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  alignItems: "center",
                }}
              >
                <div>
                  <img
                    src={ZeropcIcon}
                    alt=""
                    className={styles.firstimages}
                  />
                </div>
                <div>
                  <p className={styles.prinicpalAmountText}>
                    Foreclosure Charges
                  </p>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  alignItems: "center",
                }}
              >
                <div>
                  <img
                    src={PaybackIcon}
                    alt=""
                    className={styles.images}
                  />
                </div>
                <div>
                  <p className={styles.prinicpalAmountText}>
                    Pay Back Anytime
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.content}>
            <p
              style={{
                fontWeight: 600,
                fontSize: "1em",
              }}
            >
              Your EMI plan
            </p>
            <div
              style={{
                marginLeft: "20px",
                marginBottom: "10px",
                borderRadius: "10px",
                background: "#FFF",
                height: "8em",
                width: "18em",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <p
                style={{
                  fontWeight: "bold",
                  fontSize: "1.3em",
                  textAlign: "center",
                }}
              >
                {newFormatToIndianRupees(product?.emi || 0)}
              </p>
              <p
                style={{
                  fontWeight: "bold",
                  fontSize: "1em",
                  textAlign: "center",
                }}
              >
                {product?.productDetails?.tenure} months
              </p>
              <p
                style={{
                  fontWeight: "bold",
                  fontSize: "1em",
                  textAlign: "center",
                }}
              >
                Start Date: {moment(product?.emiFirstDate).format("DD/MM/YYYY")}
              </p>
            </div>
          </div>
          <div className={styles.content}>
            <p>Sanction Letter: </p>
            <p
              className={styles.downloadButton}
              onClick={() => {
                fetch(
                  process.env.REACT_APP_DASHBOARD_URL + "/file/url-content-sl?path=" +
                    product?.sanctionLetterUrl?.split("amazonaws.com/")[1]
                )
                  .then((response) => response.json())
                  .then((data) => {
                    const linkSource = `${data.data}`;
                    const downloadLink = document.createElement("a");
                    const fileName = "sanction-letter.pdf";
                    downloadLink.href = linkSource;
                    downloadLink.download = fileName;
                    downloadLink.click();
                  });
              }}
            >
              Download
            </p>
          </div>
          <Button 
            insureFin={location?.state?.data?.channelId === 5}
            onPress={() => {
              navigate("/sanctions", {
                state: {
                  data: { product, ...location?.state?.data },
                },
              });
            }}
            text={"Proceed"}
          />
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default SelectEmi;